// Resolve firm branding for a client-facing surface. Inputs can be:
//   - { firm }              the payload from /u/:token or /client-home
//   - { branding }          a raw JSONB blob
// Falls back to BridgeIntoTax defaults so the page never crashes if the
// firm has not configured its own branding yet.
//
// Returns a stable object: { displayName, tagline, logoUrl, primary,
// secondary, accent, footerText, cssVars }. `cssVars` is a style object
// you can spread onto the top-level client-facing element so child
// components can read `hsl(var(--firm-primary))` etc.
import { useMemo } from 'react';

const DEFAULTS = {
  displayName: 'BridgeIntoTax',
  tagline: 'Secure tax document portal',
  logoUrl: null,
  primary: '#2f7a3a',
  secondary: '#3859b2',
  accent: '#6fb04a',
  footerText: '',
};

function hexToRgb(hex) {
  if (!hex) return null;
  const m = hex.replace('#', '');
  if (m.length !== 6 && m.length !== 3) return null;
  const full = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b };
}

// Normalise any of the shapes client surfaces carry today:
//   { firm: { primary_color, accent_color, display_name, logo_url, footer_text, ... } }
//   { firm: { name, branding: {...} } }
//   { branding: {...} }
function pickBranding(input) {
  if (!input) return {};
  if (input.branding) return input.branding;
  if (input.firm?.branding) {
    return { ...input.firm, ...input.firm.branding };
  }
  if (input.firm) return input.firm;
  return input;
}

export function useFirmBranding(input) {
  return useMemo(() => {
    const b = pickBranding(input);
    const displayName = b.display_name || b.displayName || b.name || DEFAULTS.displayName;
    const tagline = b.tagline || b.tag_line || DEFAULTS.tagline;
    const logoUrl = b.logo_url || b.logoUrl || DEFAULTS.logoUrl;
    const primary = b.primary_color || b.primary || DEFAULTS.primary;
    const secondary = b.secondary_color || b.accent_color || b.secondary || DEFAULTS.secondary;
    const accent = b.accent_color || b.accent || DEFAULTS.accent;
    const footerText = b.footer_text || b.footerText || DEFAULTS.footerText;

    const pRgb = hexToRgb(primary);
    const sRgb = hexToRgb(secondary);
    const cssVars = {
      '--firm-primary': primary,
      '--firm-secondary': secondary,
      '--firm-accent': accent,
    };
    if (pRgb) cssVars['--firm-primary-rgb'] = `${pRgb.r} ${pRgb.g} ${pRgb.b}`;
    if (sRgb) cssVars['--firm-secondary-rgb'] = `${sRgb.r} ${sRgb.g} ${sRgb.b}`;

    return { displayName, tagline, logoUrl, primary, secondary, accent, footerText, cssVars };
  }, [input]);
}
